import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Paddock69Logo } from "@/components/ui/Paddock69Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useUserGroups } from "@/hooks/useGroupMembership";
import { useAllGroups, useMyJoinRequests } from "@/hooks/useGroupMembership";
import { useGroups } from "@/hooks/useGroups";
import {
  Users, Plus, Search, Loader2, Clock, Check,
  ChevronRight, Image as ImageIcon, Send,
} from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";

interface GroupGateProps {
  children: React.ReactNode;
}

export function GroupGate({ children }: GroupGateProps) {
  const { hasGroup, loading } = useUserGroups();

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!hasGroup) {
    return <GroupOnboarding />;
  }

  return <>{children}</>;
}

function GroupOnboarding() {
  const [mode, setMode] = useState<"choose" | "create" | "browse">("choose");
  const { createGroup } = useGroups();
  const { groups, loading: groupsLoading } = useAllGroups();
  const { requests, sendJoinRequest } = useMyJoinRequests();
  const { refetch } = useUserGroups();

  // Create group state
  const [newName, setNewName] = useState("");
  const [newDesc, setNewDesc] = useState("");
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  // Join request state
  const [joinMessage, setJoinMessage] = useState("");
  const [selectedGroup, setSelectedGroup] = useState<any | null>(null);
  const [joinDialogOpen, setJoinDialogOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [sending, setSending] = useState(false);

  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setLogoFile(file);
    setLogoPreview(URL.createObjectURL(file));
  };

  const handleCreate = async () => {
    if (!newName.trim()) return;
    setCreating(true);
    const group = await createGroup(newName.trim(), newDesc.trim() || undefined, logoFile || undefined);
    if (group) {
      await refetch();
    }
    setCreating(false);
  };

  const handleSendRequest = async () => {
    if (!selectedGroup) return;
    setSending(true);
    await sendJoinRequest(selectedGroup.id, joinMessage.trim() || undefined);
    setSending(false);
    setJoinDialogOpen(false);
    setJoinMessage("");
    setSelectedGroup(null);
  };

  const pendingRequestGroupIds = new Set(
    requests.filter((r: any) => r.status === "pending").map((r: any) => r.group_id)
  );

  const filteredGroups = groups.filter((g) =>
    g.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-background relative overflow-hidden">
      
      <div className="absolute inset-0 grid-pattern opacity-20" />

      <div className="relative z-10 min-h-screen flex flex-col items-center justify-center p-6 max-w-md mx-auto">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <Paddock69Logo size="md" animated showText />
        </motion.div>

        <AnimatePresence mode="wait">
          {mode === "choose" && (
            <motion.div
              key="choose"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="w-full space-y-4 text-center"
            >
              <h2 className="font-mono font-bold text-xl text-foreground">
                ¡Bienvenido al Paddock!
              </h2>
              <p className="text-sm text-muted-foreground">
                Para empezar necesitas unirte a una peña motera o crear la tuya propia.
              </p>

              <div className="space-y-3 pt-4">
                <Button
                  onClick={() => setMode("browse")}
                  className="w-full btn-racing h-12 text-sm gap-2"
                >
                  <Search className="w-4 h-4" />
                  Buscar grupo y solicitar unirme
                </Button>

                <Button
                  onClick={() => setMode("create")}
                  variant="outline"
                  className="w-full h-12 text-sm gap-2 border-primary/30 hover:bg-primary/10"
                >
                  <Plus className="w-4 h-4" />
                  Crear mi propia peña
                </Button>
              </div>

              {/* Pending requests */}
              {requests.filter((r: any) => r.status === "pending").length > 0 && (
                <div className="mt-6 card-telemetry text-left">
                  <p className="label-technical mb-2 flex items-center gap-1.5">
                    <Clock className="w-3.5 h-3.5" />
                    Solicitudes pendientes
                  </p>
                  {requests
                    .filter((r: any) => r.status === "pending")
                    .map((r: any) => {
                      const group = groups.find((g) => g.id === r.group_id);
                      return (
                        <div key={r.id} className="flex items-center gap-2 py-1.5">
                          <Clock className="w-3.5 h-3.5 text-accent flex-shrink-0" />
                          <span className="text-sm font-mono text-foreground truncate">
                            {group?.name || "Grupo"}
                          </span>
                          <span className="text-[10px] font-mono text-accent ml-auto">PENDIENTE</span>
                        </div>
                      );
                    })}
                </div>
              )}
            </motion.div>
          )}

          {mode === "browse" && (
            <motion.div
              key="browse"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="w-full space-y-4"
            >
              <div className="flex items-center gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setMode("choose")}
                  className="text-muted-foreground"
                >
                  ← Volver
                </Button>
                <h2 className="font-mono font-bold text-lg text-foreground">Grupos disponibles</h2>
              </div>

              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Buscar grupo..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="input-paddock pl-9"
                />
              </div>

              {groupsLoading ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-primary" />
                </div>
              ) : filteredGroups.length === 0 ? (
                <div className="text-center py-8">
                  <Users className="w-8 h-8 mx-auto mb-2 text-muted-foreground" />
                  <p className="text-sm text-muted-foreground">No hay grupos disponibles</p>
                </div>
              ) : (
                <div className="space-y-2 max-h-[50vh] overflow-y-auto">
                  {filteredGroups.map((group) => {
                    const hasPending = pendingRequestGroupIds.has(group.id);
                    return (
                      <div
                        key={group.id}
                        className="card-telemetry flex items-center gap-3"
                      >
                        <div className="w-10 h-10 rounded-xl bg-secondary overflow-hidden flex-shrink-0">
                          {group.logo_url ? (
                            <img src={group.logo_url} alt={group.name} className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-muted-foreground">
                              <Users className="w-4 h-4" />
                            </div>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="font-mono font-bold text-foreground text-sm truncate">{group.name}</h3>
                          {group.description && (
                            <p className="text-xs text-muted-foreground truncate">{group.description}</p>
                          )}
                        </div>
                        {hasPending ? (
                          <span className="text-[10px] font-mono text-accent flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            Pendiente
                          </span>
                        ) : (
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-xs border-primary/30 text-primary hover:bg-primary/10"
                            onClick={() => {
                              setSelectedGroup(group);
                              setJoinDialogOpen(true);
                            }}
                          >
                            <Send className="w-3 h-3 mr-1" />
                            Solicitar
                          </Button>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </motion.div>
          )}

          {mode === "create" && (
            <motion.div
              key="create"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="w-full space-y-4"
            >
              <div className="flex items-center gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setMode("choose")}
                  className="text-muted-foreground"
                >
                  ← Volver
                </Button>
                <h2 className="font-mono font-bold text-lg text-foreground">Crear Peña</h2>
              </div>

              <p className="text-xs text-muted-foreground">
                Mínimo recomendado: 6 miembros para una peña activa.
              </p>

              {/* Logo upload */}
              <div className="flex justify-center">
                <label className="cursor-pointer group">
                  <div className="w-24 h-24 rounded-2xl bg-secondary border-2 border-dashed border-border hover:border-primary/50 transition-colors flex items-center justify-center overflow-hidden">
                    {logoPreview ? (
                      <img src={logoPreview} alt="Logo" className="w-full h-full object-cover" />
                    ) : (
                      <div className="flex flex-col items-center gap-1 text-muted-foreground group-hover:text-foreground transition-colors">
                        <ImageIcon className="w-6 h-6" />
                        <span className="text-[10px] font-mono">LOGO</span>
                      </div>
                    )}
                  </div>
                  <input type="file" accept="image/*" className="hidden" onChange={handleLogoChange} />
                </label>
              </div>

              <div className="space-y-1.5">
                <Label className="label-technical">Nombre del grupo</Label>
                <Input
                  placeholder="Ej: Peña Motera Los 69"
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  className="input-paddock"
                />
              </div>

              <div className="space-y-1.5">
                <Label className="label-technical">Descripción (opcional)</Label>
                <Input
                  placeholder="¿De qué va vuestro grupo?"
                  value={newDesc}
                  onChange={(e) => setNewDesc(e.target.value)}
                  className="input-paddock"
                />
              </div>

              <Button
                onClick={handleCreate}
                disabled={!newName.trim() || creating}
                className="w-full btn-racing h-12"
              >
                {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : "Crear Grupo"}
              </Button>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Join request dialog */}
        <Dialog open={joinDialogOpen} onOpenChange={setJoinDialogOpen}>
          <DialogContent className="bg-card border-border max-w-sm mx-auto">
            <DialogHeader>
              <DialogTitle className="font-mono text-foreground">
                Solicitar unirse a {selectedGroup?.name}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-2">
              <div className="space-y-1.5">
                <Label className="label-technical">Mensaje (opcional)</Label>
                <Input
                  placeholder="Ej: Soy amigo de Juan, quiero unirme al grupo"
                  value={joinMessage}
                  onChange={(e) => setJoinMessage(e.target.value)}
                  className="input-paddock"
                />
              </div>
              <Button
                onClick={handleSendRequest}
                disabled={sending}
                className="w-full btn-racing"
              >
                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : "Enviar solicitud"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
